// Air France — Transition Landing Page content
// Two-language structure: en (default English) + fr (Français)
// Used by /app/frontend/src/pages/transition/AirlineTransitionPage.js

const airFrance = {
  slug: 'air-france',
  airline: 'Air France',
  brandHue: '#0F2A5F', // Air France navy accent — used sparingly
  hubs: ['CDG', 'ORY'],
  heroImage:
    'https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/4lcxvycf_ChatGPT%20Image%20May%2028%2C%202026%20at%2001_44_39%20PM.png',

  // ---------------------------------------------------------------
  // EN — English version
  // ---------------------------------------------------------------
  en: {
    lang: 'en',
    path: '/transition/air-france',
    altLangPath: '/transition/air-france/fr',
    altLangLabel: 'Lire en français',

    metaTitle: 'From Air France to Private Aviation | Gate 33',
    metaDescription:
      'Air France cabin crew transitioning to private aviation: what changes, what operators expect, and how Gate 33 prepares you for the real operational layer.',

    eyebrow: 'For Air France Cabin Crew',
    title: 'From Air France to Private Aviation.',
    subtitle:
      'You already deliver service at the highest commercial standard. Private aviation is a different discipline — and we exist to take you across that line.',

    introTitle: 'You are not starting from zero. You are switching disciplines.',
    introText: [
      'Air France gave you the service polish, the long-haul stamina, the languages, and the calm under pressure that operators notice immediately. That is the foundation — not the destination.',
      'What changes in private aviation is the operating model. No senior, no procedure manual, no galley team. You become the entire cabin — sometimes the only crew member on board. Operators do not hire for hours flown. They hire for autonomy, judgement, and the ability to deliver UHNW service alone.',
    ],

    whyTitle: 'Why Air France crew make the switch',
    whyItems: [
      {
        kicker: 'Compensation',
        title: 'A different economic reality',
        body: 'Private flight attendants are routinely paid 2–4× their commercial counterparts, with day rates on freelance contracts that simply do not exist in scheduled airlines.',
      },
      {
        kicker: 'Lifestyle',
        title: 'No more rosters. No more standby.',
        body: 'Trips are planned. Most flights are one to three sectors. You sleep in five-star hotels, not crew rest. You return home with proper recovery — not back-to-back rotations.',
      },
      {
        kicker: 'Craft',
        title: 'You finally serve guests, not passengers',
        body: 'Two to four people in a cabin you have prepared yourself. Service becomes hospitality. Discretion becomes a skill. The job becomes the one you actually trained for.',
      },
      {
        kicker: 'Geography',
        title: 'CDG and ORY are gateways',
        body: 'Paris is one of the most active private aviation hubs in Europe. Operators in LBG, CDG, Geneva, and the South of France hire continuously — provided you arrive with the right operational profile.',
      },
    ],

    gapTitle: 'The gap nobody at Air France will tell you about',
    gapText: [
      'Airline training teaches you to follow procedures. Private aviation requires you to create them. There is no SOP for catering a kosher meal at 4am from a small FBO. No checklist for managing a passenger change 90 minutes before departure.',
      'The skills that get you hired — solo operational capability, catering ordering, galley competence across multiple aircraft types, discretion around UHNW passengers — are exactly the skills no commercial carrier ever develops. That is the gap we close.',
    ],

    programTitle: 'What Gate 33 builds with you',
    programText:
      'Gate 33 is the operational training Air France never had a reason to give you. We translate your existing service intelligence into the private-aviation operating model — through the program, the consulting, and the tools we built ourselves.',
    programItems: [
      'The full operational cycle of a private flight — brief, catering, galley, service, recovery',
      'Aircraft-by-aircraft fluency (Global, Falcon, Challenger, Gulfstream, Embraer)',
      'UHNW service codes, discretion protocols, and reading the cabin',
      'Crisis judgement: 27 real-world scenarios decoded the way premium operators expect',
      'Your CV and positioning — rebuilt for operators, not airline HR',
    ],

    // 3-Day Online Training — updated block
    academyTitle: 'Join the 3-Day Online Training — €600, from anywhere',
    academyText:
      'The flagship Gate 33 training is now fully online — 3 consecutive live online days with your trainer, covering 14 modules. No recorded videos. It compresses what most cabin crew spend years figuring out on their own: operational autonomy, catering logic, aircraft fluency, and the recruitment positioning operators actually respond to. €600, or 3 × €200.',
    academyCtas: [
      {
        label: 'Discover the 3-Day Online Training',
        href: '/academy',
        testId: 'airline-academy-cta-main',
        variant: 'primary',
      },
      {
        label: 'Standalone Catering & Operations Training',
        href: '/academy/catering-operations',
        testId: 'airline-academy-cta-catering',
        variant: 'ghost',
      },
    ],

    cvTitle: 'Your CV is the first cabin you walk into.',
    cvText: [
      'Operators do not read airline CVs the way airline HR does. A two-page document listing fleet types and seniority will be filtered out in seconds. What works is a single-page operator-grade profile that signals autonomy, mobility, and service intelligence.',
      'We rebuild it with you, line by line, so that when an operator opens the file they see a private flight attendant — not an airline FA hoping for a chance.',
    ],
    cvCta: {
      label: 'Upgrade your profile',
      href: '/mentorship',
      testId: 'airline-cv-cta',
    },

    communityTitle: 'Inside the room before you apply.',
    communityText:
      'The Gate 33 community gathers cabin crew already inside private aviation and those preparing the transition. Operator listings, real cabin debriefs, recruiter conversations — the kind of access airline forums simply do not have.',
    communityCtas: [
      {
        label: 'Join the WhatsApp community',
        href: 'https://chat.whatsapp.com/Cy777tVbX5h4L4YOBn5FoI?mode=gi_t',
        kind: 'whatsapp',
        testId: 'airline-cta-whatsapp',
      },
      {
        label: 'Follow Gate 33 on LinkedIn',
        href: 'https://www.linkedin.com/company/gate-33/',
        kind: 'linkedin',
        testId: 'airline-cta-linkedin',
      },
    ],

    finalCtaTitle: 'Air France gave you the standard. We give you the next altitude.',
    finalCtaText:
      'The transition is not a leap. It is a re-engineering of your operational profile. The 3-Day Online Training is the most direct path through it — €600, three live online days with your trainer.',
    finalCtaLabel: 'See the next cohort',
    finalCtaHref: '/academy',
    finalCtaTestId: 'airline-final-cta',
  },

  // ---------------------------------------------------------------
  // FR — Français
  // ---------------------------------------------------------------
  fr: {
    lang: 'fr',
    path: '/transition/air-france/fr',
    altLangPath: '/transition/air-france',
    altLangLabel: 'Read in English',

    metaTitle: 'D’Air France à l’Aviation Privée | Gate 33',
    metaDescription:
      'PNC Air France en transition vers l’aviation privée : ce qui change, ce que les opérateurs attendent vraiment, et la manière dont Gate 33 vous prépare à la couche opérationnelle réelle.',

    eyebrow: 'Pour les PNC Air France',
    title: 'D’Air France à l’Aviation Privée.',
    subtitle:
      'Vous délivrez déjà un service au plus haut standard commercial. L’aviation privée est une autre discipline — et nous existons pour vous faire passer cette ligne.',

    introTitle: 'Vous ne partez pas de zéro. Vous changez de discipline.',
    introText: [
      'Air France vous a donné le sens du service, l’endurance long-courrier, les langues et le calme sous pression que les opérateurs remarquent immédiatement. C’est la base — pas la destination.',
      'Ce qui change en aviation privée, c’est le modèle opérationnel. Plus de chef de cabine, plus de manuel de procédure, plus d’équipe galley. Vous devenez l’intégralité de la cabine — parfois la seule personne à bord. Les opérateurs ne recrutent pas sur les heures de vol. Ils recrutent sur l’autonomie, le jugement et la capacité à délivrer un service UHNW seul·e.',
    ],

    whyTitle: 'Pourquoi les PNC Air France franchissent le pas',
    whyItems: [
      {
        kicker: 'Rémunération',
        title: 'Une réalité économique différente',
        body: 'Les PNC privé sont régulièrement rémunérés 2 à 4× plus que leurs homologues commerciaux, avec des tarifs journaliers en freelance qui n’existent simplement pas en compagnie régulière.',
      },
      {
        kicker: 'Rythme de vie',
        title: 'Fin des plannings. Fin du standby.',
        body: 'Les vols sont planifiés. La majorité fait un à trois secteurs. Vous dormez dans des hôtels 5*, pas en crew rest. Vous rentrez avec un vrai temps de récupération — pas des rotations enchaînées.',
      },
      {
        kicker: 'Métier',
        title: 'Vous servez enfin des invités, pas des passagers',
        body: 'Deux à quatre personnes dans une cabine que vous avez préparée vous-même. Le service devient hospitalité. La discrétion devient une compétence. Le métier devient celui pour lequel vous étiez réellement faite.',
      },
      {
        kicker: 'Géographie',
        title: 'CDG et ORY sont des portes d’entrée',
        body: 'Paris est l’un des hubs les plus actifs d’aviation privée en Europe. Les opérateurs basés à LBG, CDG, Genève et dans le Sud de la France recrutent en continu — à condition d’arriver avec le bon profil opérationnel.',
      },
    ],

    gapTitle: 'Le fossé que personne chez Air France ne vous dira',
    gapText: [
      'La formation compagnie vous apprend à suivre des procédures. L’aviation privée exige que vous les créiez. Aucune procédure pour un catering casher à 4h du matin depuis un petit FBO. Aucune checklist pour gérer un changement de passager 90 minutes avant le départ.',
      'Les compétences qui font qu’on vous recrute — autonomie opérationnelle, gestion du catering, maîtrise galley sur plusieurs types d’avions, discrétion auprès des passagers UHNW — sont précisément celles qu’aucune compagnie commerciale ne développe jamais. C’est le fossé que nous comblons.',
    ],

    programTitle: 'Ce que Gate 33 construit avec vous',
    programText:
      'Gate 33 est la formation opérationnelle qu’Air France n’a jamais eu de raison de vous donner. Nous traduisons votre intelligence de service existante dans le modèle opérationnel de l’aviation privée — à travers le programme, le consulting et les outils que nous avons construits nous-mêmes.',
    programItems: [
      'Le cycle opérationnel complet d’un vol privé — briefing, catering, galley, service, récupération',
      'La maîtrise avion par avion (Global, Falcon, Challenger, Gulfstream, Embraer)',
      'Les codes de service UHNW, les protocoles de discrétion, la lecture de la cabine',
      'Le jugement en situation de crise : 27 scénarios réels décodés comme l’attendent les opérateurs premium',
      'Votre CV et votre positionnement — reconstruits pour les opérateurs, pas pour les RH compagnie',
    ],

    academyTitle: 'Rejoignez le 3-Day Online Training — €600, depuis n’importe où',
    academyText:
      'Le programme phare Gate 33 est désormais entièrement en ligne — 3 journées consécutives en direct avec votre formateur, couvrant 14 modules. Pas de vidéos enregistrées. Il compresse ce que la plupart des PNC mettent des années à comprendre seuls : autonomie opérationnelle, logique du catering, maîtrise des avions et positionnement de recrutement auquel les opérateurs répondent vraiment. €600, ou 3 × €200.',
    academyCtas: [
      {
        label: 'Découvrir l’Academy 3 jours',
        href: '/academy',
        testId: 'airline-academy-cta-main-fr',
        variant: 'primary',
      },
      {
        label: 'Formation Catering & Operations',
        href: '/academy/catering-operations',
        testId: 'airline-academy-cta-catering-fr',
        variant: 'ghost',
      },
    ],

    cvTitle: 'Votre CV est la première cabine dans laquelle vous entrez.',
    cvText: [
      'Les opérateurs ne lisent pas un CV compagnie comme le ferait un service RH. Un document de deux pages listant flottes et anciennetés est filtré en quelques secondes. Ce qui fonctionne, c’est un profil d’une page, calibré opérateur, qui signale autonomie, mobilité et intelligence de service.',
      'Nous le reconstruisons avec vous, ligne par ligne, pour qu’à l’ouverture du fichier un opérateur voie un·e PNC privé·e — pas un·e PNC compagnie en quête d’une opportunité.',
    ],
    cvCta: {
      label: 'Optimiser mon profil',
      href: '/mentorship',
      testId: 'airline-cv-cta-fr',
    },

    communityTitle: 'Dans la pièce, avant même de postuler.',
    communityText:
      'La communauté Gate 33 réunit les PNC déjà en aviation privée et celles et ceux qui préparent la transition. Annonces opérateurs, débriefs cabine réels, conversations avec recruteurs — un accès que les forums compagnie n’offrent simplement pas.',
    communityCtas: [
      {
        label: 'Rejoindre la communauté WhatsApp',
        href: 'https://chat.whatsapp.com/Cy777tVbX5h4L4YOBn5FoI?mode=gi_t',
        kind: 'whatsapp',
        testId: 'airline-cta-whatsapp-fr',
      },
      {
        label: 'Suivre Gate 33 sur LinkedIn',
        href: 'https://www.linkedin.com/company/gate-33/',
        kind: 'linkedin',
        testId: 'airline-cta-linkedin-fr',
      },
    ],

    finalCtaTitle: 'Air France vous a donné le standard. Nous vous donnons l’altitude suivante.',
    finalCtaText:
      'La transition n’est pas un saut. C’est une réingénierie de votre profil opérationnel. Le 3-Day Online Training est le chemin le plus direct — €600, 3 journées en direct avec votre formateur.',
    finalCtaLabel: 'Voir la prochaine session',
    finalCtaHref: '/academy',
    finalCtaTestId: 'airline-final-cta-fr',
  },
};

export default airFrance;
